<template>
  <div>
    <!--
      Auth routes (login / forgotPassword) render full-screen with no app shell. Their components
      are the route component itself, so they come straight out of the router-view.
    -->
    <router-view v-if="isAuthRoute" />

    <!--
      `v-else-if="user"`: the shell renders only once the session has landed. Without it the profile
      and menu painted empty for a frame on a fresh authed load - the old Angular layout was gated
      on `authPath`, which the guard set after the user, so it never showed that frame. The specs
      read #loggedInUser as soon as it appears, and a frame of "" was the failure.
    -->
    <div v-else-if="user" class="gray-bg">
      <div id="wrapper">
        <nav class="navbar-default navbar-static-side" role="navigation">
          <ProfileComponent :user="user" :roles="userRoles" v-on:edit="editProfile" v-on:home="goHome" />
          <CalendarComponent :calendar-date="calendarDate" v-on:set-calendar-date="setCalendarDate" />
          <MenuComponent
            :today-class="todayClass"
            :incomplete-bookings-count="incompleteBookingsCount"
            :active-state="activeState"
            :support-unread="supportUnread"
            v-on:today="goToday"
            v-on:open-incomplete-bookings="openIncompleteBookings"
            v-on:navigate="navigate"
            v-on:open-support="openSupport"
            v-on:logout="logout"
          />
        </nav>

        <div id="page-wrapper" :class="{ 'support-open': supportOpen }">
          <!-- The search bar is only on the resources page (the old `abstract-mainLayout`). -->
          <template v-if="showSearch">
            <div class="row fixed-top mainContentHeader">
              <div class="col-12">
                <div class="m-b">
                  <SearchComponent
                    :job-selected="jobSelected"
                    :is-producer="isProducer"
                    :is-admin="isAdmin"
                    :job-window-open="jobWindowOpen"
                    :find-job="findJob"
                    :label="label"
                    v-on:select-job="selectJob"
                    v-on:go-to-job="goToJob"
                    v-on:go-to-quote="goToQuote"
                    v-on:add-job="addJob"
                  />
                </div>
              </div>
            </div>

            <div class="row" style="margin-top: 80px">
              <div class="col-12">
                <router-view />
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <div class="footer mainContentFooter">
                  <FooterComponent
                    :version="version"
                    :timezone="timezone"
                    :is-admin="isAdmin"
                    v-on:go-to-diagnostics="goToDiagnostics"
                  />
                </div>
              </div>
            </div>
          </template>

          <!-- Everything else uses the no-search shell (`abstract-mainLayoutNoSearch`). -->
          <template v-else>
            <div class="m-t">
              <router-view />
            </div>
            <div class="footer mainContentFooter">
              <FooterComponent
                :version="version"
                :timezone="timezone"
                :is-admin="isAdmin"
                v-on:go-to-diagnostics="goToDiagnostics"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <!--
      The Vue slide-out host (step 15b). Always mounted: it registers itself on the bridge for
      SliderService, and renders the booking / incomplete-bookings panels when opened. The
      resource-selection slider is still an Angular template and uses $aside.
    -->
    <!-- In-app support (#664). On every signed-in page; the launcher floats bottom-right. -->
    <SupportWidget v-if="user" />

    <SliderHost />

    <!--
      The toast host (step 27). Always mounted; renders the alerts queued by alertService, which
      replaced the AlertService Angular factory and toastr's markup.
    -->
    <ToastHost />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { defineAsyncComponent } from 'vue'

import userStore from './common/service/userStore.js'
import searchService from './search/service/searchService.js'
import footerService from './footer/service/footerService.js'
import resourcesService from './resources/service/resourcesService.js'
import jobService from './job/service/jobService.js'
import loginService from './login/service/loginService.js'
import roleService from './login/service/roleService.js'
import calendarService from './leftCalendar/service/calendarService.js'
import ProfileComponent from './profile/component/profileComponent.vue'
import CalendarComponent from './leftCalendar/component/calendarComponent.vue'
import MenuComponent from './menu/component/menuComponent.vue'
const SearchComponent = defineAsyncComponent(() => import('./search/component/searchComponent.vue'))
import FooterComponent from './footer/component/footerComponent.vue'
import SliderHost from './common/component/sliderHost.vue'
import ToastHost from './common/component/toastHost.vue'
const SupportWidget = defineAsyncComponent(() => import('./support/component/supportWidget.vue'))

// The vue-router route name -> the ui-router state name the menu links still compare against, so
// MenuComponent's `active` class does not have to change in this step. Empty for routes with no
// menu entry (the settings tabs all highlight Settings).
const ACTIVE_STATE = {
  resources: 'router-resources',
  reporting: 'router-reporting',
  documents: 'router-documents',
  quoteSheet: 'router-quoteSheet',
  'settings-profile': 'router-settings',
  'settings-resources': 'router-settings',
  'settings-clients': 'router-settings',
  'settings-prefix': 'router-settings',
  'settings-quoteTemplate': 'router-settings',
  'settings-testExternal': 'router-settings',
}

// The app shell (step 24 of #614). This was `mainLayout.pug` / `mainLayoutNoSearch.pug` plus
// MenuCtrl / ProfileCtrl / LeftCalendarCtrl / SearchCtrl / FooterCtrl, all of which owned their
// data through Angular. It is now the Vue root, rendered by the router, and the same components
// the ngVue bridge used to feed.
//
// The services whose state this reacts to are Vue.observable now (step 41 of #614), so everything
// here is an ordinary Vue watcher.
export default {
  name: 'App',
  components: {
    ProfileComponent,
    CalendarComponent,
    MenuComponent,
    SearchComponent,
    FooterComponent,
    SliderHost,
    ToastHost,
    SupportWidget,
  },
  data() {
    return {
      user: userStore.getUser(),
      isAdmin: false,
      isExperimenter: false,
      isProducer: false,
      userRoles: [],
      calendarDate: calendarService.getSelectedDate(),
      incompleteBookingsCount: 0,
      jobSelected: !!jobService.getCurrentJob(),
      jobWindowOpen: jobService.getJobWindowOpen(),
      version: {},
      timezone: `${Intl.DateTimeFormat().resolvedOptions().timeZone} - ${new Date().toString()}`,
      stopWatching: [],
      stopUserWatch: () => {},
    }
  },
  computed: {
    isAuthRoute() {
      return this.$route.matched.some(record => record.meta && record.meta.authRoot)
    },
    showSearch() {
      return this.$route.name === 'resources'
    },
    activeState() {
      return ACTIVE_STATE[this.$route.name] || ''
    },
    todayClass() {
      const onResourcesViewingToday = this.$route.name === 'resources' && this.isToday(this.calendarDate)
      return onResourcesViewingToday ? '' : 'active'
    },
    supportUnread() {
      return this.$store.getters['support/unread']
    },
    supportOpen() {
      return this.$store.getters['support/isOpen']
    },
  },
  created() {
    this.applyRoles()
    // The boot session check sets the user asynchronously; roles are read from it.
    this.stopUserWatch = this.$watch(
      () => userStore.getUser(),
      user => {
        this.user = user
        this.applyRoles()
      }
    )

    footerService.version().then(result => {
      this.version = result.data
    })

    this.stopWatching = [
      // A plain module since step 33, so this is an ordinary Vue watcher rather than an
      // $rootScope.$watch that only noticed a change when a digest ran.
      this.$watch(
        () => calendarService.getSelectedDate(),
        date => {
          this.calendarDate = date
        }
      ),
      // Watch the length, not the array: the count is filled in later.
      this.$watch(
        () => resourcesService.getIncompleteBookings().length,
        count => {
          this.incompleteBookingsCount = count
        }
      ),
      this.$watch(
        () => jobService.getCurrentJob(),
        job => {
          this.jobSelected = !!job
        }
      ),
      this.$watch(
        () => jobService.getJobWindowOpen(),
        open => {
          this.jobWindowOpen = open
        }
      ),
    ]
  },
  beforeUnmount() {
    this.stopWatching.forEach(stop => stop())
    this.stopUserWatch()
  },
  methods: {
    applyRoles() {
      this.isAdmin = roleService.hasRole('admin')
      this.isExperimenter = roleService.hasRole('experimental')
      this.isProducer = roleService.hasRole('producer')
      this.userRoles = roleService.getUserRoles() || []
    },

    // ---- profile ----
    editProfile() {
      this.$router.push('/settings/profile').catch(() => {})
    },
    goHome() {
      this.$router.push('/').catch(() => {})
    },

    // ---- left calendar ----
    setCalendarDate(date) {
      calendarService.setSelectedDate(date)
    },

    // ---- menu ----
    // A local-calendar day comparison. The shell used moment-timezone only for this and the
    // footer label; Intl + Date keeps it out of the initial shell chunk.
    isToday(value) {
      const date = new Date(value)
      const now = new Date()
      return (
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate()
      )
    },
    goToday() {
      // Today may not change the route, so the widget's route watcher would not fire. Close the
      // support panel here too - moving menu item means leaving the conversation.
      this.$store.dispatch('support/closeSupport')
      calendarService.setSelectedDate(new Date())
      this.$router.push('/').catch(() => {})
    },
    openIncompleteBookings() {
      resourcesService.openIncompleteBookings()
    },
    // The Support menu item toggles the slideout: clicking it again closes the panel it opened.
    openSupport() {
      this.$store.dispatch(this.supportOpen ? 'support/closeSupport' : 'support/openSupport')
    },
    navigate(target) {
      const path = {
        'router-resources': '/',
        'router-reporting': '/reporting',
        'router-documents': '/documents',
        'router-settings': '/settings/profile',
      }[target]

      if (path === '/settings/profile') {
        this.$router.push('/settings/profile').catch(() => {})
        return
      }

      if (path) {
        this.$router.push(path).catch(() => {})
      }
    },
    async logout() {
      this.user = undefined

      let leaving = false
      try {
        // True when the service has sent the browser to Cognito's logout endpoint. That is how the
        // Hosted UI's own session ends, and navigating anywhere else here would cancel it (#677).
        leaving = (await loginService.logout()) === true
      } catch (err) {
        console.log('Error logging out', err)
      }

      if (leaving) {
        return
      }

      // A full load resets the app so the next login starts clean.
      window.location.assign('/login')
    },

    // ---- search ----
    findJob(query) {
      return searchService.findJobWithText(query, false)
    },
    label(item) {
      return searchService.label(item)
    },
    selectJob(job) {
      this.$router.push('/').catch(() => {})
      jobService.setCurrentJob(cloneDeep(job))
    },
    goToJob() {
      jobService.openJobWindow()
    },
    goToQuote() {
      this.$router.push({ path: '/quoting', query: { from: 'search' } }).catch(() => {})
    },
    async addJob() {
      this.$router.push('/').catch(() => {})
      // `addJob` reads the user (the producer) and the role (the starting status), and the boot
      // session check that sets the user is async - wait for it, or the job is created with no
      // producer and the wrong status.
      if (!userStore.getUser()) {
        try {
          const session = await loginService.currentSession()
          userStore.setUser(session.tokens.idToken.payload)
        } catch (err) {
          // not signed in - the interceptor will send them to login
        }
      }
      jobService.addJob({ openWindow: true })
    },

    // ---- footer ----
    goToDiagnostics() {
      this.$router.push('/diagnostics').catch(() => {})
    },
  },
}
</script>
